'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi } from '@/providers/api-provider'
import type { Squadron } from '@/lib/api/types'

export function useMySquadrons() {
  const api = useApi()
  return useQuery({
    queryKey: ['my-squadrons'],
    queryFn: () => api.admiral.mySquadrons(),
  })
}

export function useSquadron(id: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['squadrons', id],
    queryFn: () => api.admiral.squadron(id),
    enabled: !!id,
  })
}

export function useSquadronProjects(squadronId: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['squadrons', squadronId, 'projects'],
    queryFn: () => api.admiral.projects(squadronId),
    enabled: !!squadronId,
  })
}

export function useSquadronCrew(squadronId: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['squadrons', squadronId, 'crew'],
    queryFn: () => api.admiral.crew(squadronId),
    enabled: !!squadronId,
  })
}

export function useAdminSquadrons() {
  const api = useApi()
  return useQuery({
    queryKey: ['admin', 'squadrons'],
    queryFn: () => api.squadronAdmin.list(),
  })
}

export function useCreateSquadron() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; description?: string; admiral_id: string }) =>
      api.squadronAdmin.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'squadrons'] })
    },
  })
}

export function useUpdateSquadron() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Squadron> }) =>
      api.squadronAdmin.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'squadrons'] })
      qc.invalidateQueries({ queryKey: ['squadrons'] })
    },
  })
}
